import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { ApiError, type CastMe, lineVerify } from "@/lib/api.ts";
import { getLineIdToken, loginWithLine } from "@/lib/liff.ts";

type State =
	| { kind: "loading" }
	| { kind: "signed_in"; cast: CastMe }
	| { kind: "not_registered" }
	| { kind: "line_login_required" }
	| { kind: "error"; message: string };

export function Home() {
	const [, setLocation] = useLocation();
	const [state, setState] = useState<State>({ kind: "loading" });

	useEffect(() => {
		let cancelled = false;

		(async () => {
			try {
				const idToken = await getLineIdToken();
				if (!idToken) {
					if (!cancelled) setState({ kind: "line_login_required" });
					return;
				}
				const cast = await lineVerify(idToken);
				if (!cancelled) {
					setState({ kind: "signed_in", cast });
					setLocation("/shifts");
				}
			} catch (err) {
				if (cancelled) return;
				if (err instanceof ApiError && err.status === 404) {
					setState({ kind: "not_registered" });
					return;
				}
				setState({
					kind: "error",
					message: err instanceof Error ? err.message : "予期しないエラーが発生しました",
				});
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [setLocation]);

	if (state.kind === "loading" || state.kind === "signed_in") {
		return <LoadingScreen />;
	}
	if (state.kind === "line_login_required") {
		return (
			<CenteredCard
				title="LINE ログインが必要です"
				body="ログインするとこの画面に戻ります。"
				action={{
					label: "LINE でログイン",
					onClick: () => loginWithLine(window.location.href),
				}}
			/>
		);
	}
	if (state.kind === "not_registered") {
		return (
			<CenteredCard
				title="キャスト登録が必要です"
				body="お店から届いた招待リンクを LINE 内で開いて登録を進めてください。"
			/>
		);
	}
	return <CenteredCard title="エラー" body={state.message} />;
}

function LoadingScreen() {
	return (
		<main className="flex min-h-screen items-center justify-center">
			<p className="text-sm text-gray-600">読み込み中...</p>
		</main>
	);
}

function CenteredCard({
	title,
	body,
	action,
}: {
	title: string;
	body: string;
	action?: { label: string; onClick: () => void };
}) {
	return (
		<main className="mx-auto max-w-md px-4 py-16">
			<div className="rounded-2xl bg-white p-6 shadow-sm">
				<h1 className="text-lg font-semibold">{title}</h1>
				<p className="mt-3 text-sm leading-relaxed text-gray-700">{body}</p>
				{action ? (
					<button
						type="button"
						className="mt-6 w-full rounded-xl bg-green-600 py-3 text-sm font-semibold text-white active:bg-green-700"
						onClick={action.onClick}
					>
						{action.label}
					</button>
				) : null}
			</div>
		</main>
	);
}
